import * as Yup from 'yup';
import {
  Adobesign,
  DocusignLogo,
  PandadocLogo,
  Spotdraft,
} from '@/assets/icons/ats';
import {
  AuthType,
  type ProviderConfig,
  type SpotDraftRegion,
} from '@/types/esignature';

export const ESIGNATURE_PROVIDERS = {
  DOCUSIGN: 'docusign/docusign_api',
  PANDADOC: 'pandadocs/pandadocs_api',
  ADOBE: 'adobe/adobe_api',
  SPOTDRAFT: 'spotdraft/spotdraft_api',
} as const;

/** Which default `GET /esign_envelopes/default` returns; absent means offer. */
export const ESIGN_DEFAULT_PURPOSE = {
  STAGE: 'stage',
} as const;

export const SPOTDRAFT_DEFAULT_REGION: SpotDraftRegion = 'us';

export const SPOTDRAFT_REGION_OPTIONS: {
  label: string;
  value: SpotDraftRegion;
}[] = [
  { label: 'US', value: 'us' },
  { label: 'India', value: 'in' },
  { label: 'EU', value: 'eu' },
  { label: 'Middle East', value: 'me' },
];

/**
 * Every provider shows its square mark at one size, with the name beside it as
 * text — the pattern the rest of Organization integrations already uses (Zoom,
 * HackerRank, Checkr).
 *
 * Wordmarks were the problem: each bakes its own lettering into the artwork, so
 * matching their heights still left "docusign" towering over "Adobe Sign" on
 * the same row. A mark carries no type, so one size is one size.
 */
const BRAND_MARK_SIZE = { width: '24px', height: '24px' };

export const getESignatureProvidersConfig = (): Record<
  string,
  ProviderConfig
> => {
  const config: Record<string, ProviderConfig> = {
    [ESIGNATURE_PROVIDERS.DOCUSIGN]: {
      id: ESIGNATURE_PROVIDERS.DOCUSIGN,
      label: 'Docusign',
      description:
        'Send offers to candidates and receive signatures with Docusign',
      icon: DocusignLogo,
      iconProps: BRAND_MARK_SIZE,
      authType: AuthType.OAUTH,
      integrationType: 'docusign',
      oauthConfig: {
        formMethod: 'POST',
        formUrl: '/api/v1/account_integrations/auth/docusign',
      },
    },
    [ESIGNATURE_PROVIDERS.PANDADOC]: {
      id: ESIGNATURE_PROVIDERS.PANDADOC,
      label: 'PandaDoc',
      description:
        'Send offers to candidates and receive signatures with PandaDoc',
      descriptionForm:
        'Send offer documents and collect signatures from candidates right in Kula using the PandaDoc integration',
      note: 'To integrate PandaDoc with Kula, you need a paid PandaDoc account. Get your API key from the Dev Center under Production configuration → API keys.',
      icon: PandadocLogo,
      iconProps: BRAND_MARK_SIZE,
      authType: AuthType.API_KEY,
      integrationType: 'pandadocs',
      apiKeyConfig: {
        endpoint: '/api/v1/account_integrations',
        fieldLabel: 'PandaDoc API Key',
        fieldPlaceholder: 'Enter your PandaDoc API key',
      },
    },
  };

  config[ESIGNATURE_PROVIDERS.ADOBE] = {
    id: ESIGNATURE_PROVIDERS.ADOBE,
    label: 'Adobe Sign',
    description: 'Send offers to candidates and receive signatures with Adobe',
    descriptionForm:
      'Send offer documents and collect signatures from candidates right in Kula using the Adobe Sign integration',
    note: 'To integrate Adobe Sign with Kula, you need your OAuth client credentials. Get your Client ID and Client Secret from the Adobe Developer Console.',
    icon: Adobesign,
    iconProps: BRAND_MARK_SIZE,
    authType: AuthType.OAUTH_WITH_CREDENTIALS,
    integrationType: 'adobe',
    credentialsConfig: {
      endpoint: '/api/v1/account_integrations/connect_adobe_oauth_key',
      fields: [
        {
          name: 'client_id',
          label: 'Client ID',
          placeholder: 'Enter your Adobe Sign Client ID',
          validationSchema: Yup.string().required('Client ID is required'),
        },
        {
          name: 'client_secret',
          label: 'Client Secret',
          placeholder: 'Enter your Adobe Sign Client Secret',
          validationSchema: Yup.string().required('Client Secret is required'),
        },
      ],
      oauthConfig: {
        formMethod: 'POST',
        formUrl: '/api/v1/account_integrations/auth/adobe',
      },
    },
  };

  config[ESIGNATURE_PROVIDERS.SPOTDRAFT] = {
    id: ESIGNATURE_PROVIDERS.SPOTDRAFT,
    label: 'SpotDraft',
    description:
      'Send offers to candidates and receive signatures with SpotDraft',
    descriptionForm:
      'Send offer documents and collect signatures from candidates right in Kula using the SpotDraft integration',
    note: 'Get your Client ID and Client Secret from SpotDraft under Settings → Developer Settings.',
    icon: Spotdraft,
    iconProps: BRAND_MARK_SIZE,
    authType: AuthType.CREDENTIALS_WITH_LOOKUP,
    integrationType: 'spotdraft',
  };

  return config;
};

export const PANDADOC_API_KEY_VALIDATION = {
  length: 40,
  pattern: /^[0-9A-Fa-f]+$/,
  schema: Yup.string()
    .required('API key is required')
    .length(40, 'API key must be exactly 40 characters long')
    .matches(
      /^[0-9A-Fa-f]+$/,
      'API key must contain only hexadecimal characters (0-9, A-F)'
    ),
};

export const ERROR_MESSAGES = {
  NO_AUTH_TOKEN: 'No authentication token found',
  PANDADOC_CONNECTION_FAILED:
    'PandaDoc integration failed. Verify your API key and try again',
  CREDENTIALS_VERIFICATION_FAILED:
    'integration failed. Verify your credentials and try again',
  CONNECTION_FAILED: 'Failed to connect',
  CREDENTIALS_SAVE_FAILED: 'Failed to save credentials',
  SPOTDRAFT_LOOKUP_FAILED:
    "Couldn't validate credentials. Check your Client ID, Client Secret, and Region, then try again.",
};

export const SUCCESS_MESSAGES = {
  CONNECTED: 'integration has been successfully connected.',
  CONNECTION_SUCCESS: '',
  CREDENTIALS_SAVED:
    'Credentials saved successfully. Redirecting to Adobe Sign to finish the integration.',
};

// E-sign Edit constants
export const INTEGRATION_TYPES = {
  DOCUSIGN: 'docusign',
  PANDADOC: 'pandadocs',
  ADOBE: 'adobe',
  SPOTDRAFT: 'spotdraft',
} as const;

export const RADIO_OPTIONS = {
  YES_NO: [
    { label: 'Yes', value: '1' },
    { label: 'No', value: '0' },
  ],
  SIGNING_ORDER: [
    { label: 'Employer first', value: '1' },
    { label: 'Candidate first', value: '0' },
  ],
} as const;

export const TEMPLATE_VARIABLES_VIEW = [
  'org',
  'candidate',
  'job',
  'sender',
  'offer',
];

export const ESIGN_EDITOR_PLUGINS = {
  docusign: ['link', 'image'], // Full editor with link and image plugins
  pandadocs: [], // Basic editor without link/image plugins
  adobe: [],
  spotdraft: [],
} as const;

export const USE_TEXT_EDITOR = {
  docusign: true,
  pandadocs: true,
  adobe: false,
  spotdraft: true,
} as const;

export const ESIGN_SUBJECT_MAX_LENGTH = {
  docusign: 100,
  pandadocs: 150,
  adobe: 150,
  spotdraft: 150,
} as const;

export const getIntegrationTypeParam = (
  integrationType: string
): string | undefined => {
  if (integrationType === INTEGRATION_TYPES.PANDADOC) {
    return `${integrationType}/pandadocs_api`;
  }
  if (integrationType === INTEGRATION_TYPES.ADOBE) {
    return `${integrationType}/adobe_api`;
  }
  if (integrationType === INTEGRATION_TYPES.DOCUSIGN) {
    return `${integrationType}/docusign_api`;
  }
  if (integrationType === INTEGRATION_TYPES.SPOTDRAFT) {
    return `${integrationType}/spotdraft_api`;
  }
  return undefined;
};

export const getCollapseSectionProps = () => ({
  headerConfig: {
    containerStyles: () => ({
      backgroundColor: 'ats-tertiary',
      p: '15px',
    }),
    textStyles: {
      color: 'ats-primary',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
    },
  },
  styles: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '6px',
    m: '0px -24px',
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1em 2em',
    backgroundColor: 'var(--kula-colors-white, #FFFFFF)',
  },
});

// Offer e-sign composer section headings (SectionWithHeading titles).
export const ESIGN_SECTION_TITLES = {
  DETAILS: 'E-sign details',
  MESSAGE: 'Message',
  EMAIL: 'Email',
  CANDIDATE_EMAIL: 'Candidate email',
  COMPANY_EMAIL: 'Company signer email',
  // The stage activity's name for the same block (Figma 363:27265).
  EMPLOYER_EMAIL: 'Employer email',
  CARBON_COPY_EMAIL: 'Copy recipient email',
  COPY_RECIPIENTS: 'Copy recipients',
} as const;

// Per-recipient toggle — OFF = one email to everyone, ON = a subject/body per recipient.
export const ESIGN_CUSTOM_MESSAGE_TOGGLE = {
  LABEL: 'Custom email for each recipient',
  HINT: 'Send a tailored message to the candidate, company signer, and offer copy recipients',
  STAGE_HINT:
    'Send a tailored message to the candidate, employer signer, and copy recipients',
  CANDIDATE_ONLY_HINT:
    'Only the candidate gets an email. Add an employer signer or copy recipients to tailor one for each.',
} as const;

export const ESIGN_SIGNER_PLACEHOLDER = 'Select a signer';

// DocuSign CC (CarbonCopy) recipient picker. BCC deferred — platform-disabled (444).
export const ESIGN_RECIPIENT_FIELDS = {
  CC: {
    LABEL: 'Choose who gets a copy of the signed offer',
    PLACEHOLDER: 'Add recipients',
  },
  // Stage activity: the picker sits behind a toggle and is not offer-specific.
  SHARE: {
    TOGGLE_LABEL: 'Share the signed document with additional recipients',
    LABEL: 'Select recipients',
  },
} as const;

export const ESIGN_MESSAGES = {
  UPDATE_ERROR: {
    DOCUSIGN: 'Error in updating Docusign settings',
    PANDADOC: 'Error in updating PandaDoc settings',
    ADOBE: 'Error in updating Adobe Sign settings',
  },
  UPDATE_SUCCESS: {
    DOCUSIGN: 'Docusign settings updated',
    PANDADOC: 'PandaDoc settings updated',
    ADOBE: 'Adobe Sign settings updated',
  },
  HEADER: {
    DOCUSIGN_ADMIN: 'Docusign Offer Settings',
    PANDADOC_ADMIN: 'PandaDoc Settings',
    ADOBE_ADMIN: 'Adobe Settings',
    NON_ADMIN: 'Signature Flow',
  },
  SEND_ERROR: 'Unable to send offer',
  SEND_SUCCESS: 'Offer sent',
  SUBJECT_REQUIRED: 'Subject is required',
  PREVIEW_ERROR: 'Unable to open the DocuSign preview, please try again',
  COMPANY_SIGNER_REQUIRED: 'Select a company signer before continuing',
  COMPANY_SUBJECT_REQUIRED: 'Company signer email subject is required',
  CARBON_COPY_SUBJECT_REQUIRED: 'Copy recipient email subject is required',
  CANDIDATE_INVALID_VARIABLES:
    'Remove the empty variables from the email to continue',
  COMPANY_INVALID_VARIABLES:
    'Remove the empty variables from the company signer email to continue',
  CARBON_COPY_INVALID_VARIABLES:
    'Remove the empty variables from the copy recipient email to continue',
};

// Reasons the backend can refuse a DocuSign preview, keyed by its error code. An unlisted bare code
// falls back to ESIGN_MESSAGES.PREVIEW_ERROR; a readable backend sentence passes through as-is.
export const PREVIEW_FAILURE_REASONS: Record<string, string> = {
  err_offer_docx_missing:
    'Generate the offer letter before previewing it on DocuSign',
  err_offer_candidate_details_missing:
    'Add the candidate name and email before previewing',
  err_invalid_offer_status:
    'A DocuSign draft already exists for this offer. Open it from the offer card',
  err_esign_envelope_already_present:
    'A DocuSign draft already exists for this offer. Open it from the offer card',
  err_docusign_envelope_not_ready:
    'The DocuSign draft is still being prepared. Try again in a moment',
  err_docusign_not_enabled: 'DocuSign is not connected for this account',
  err_docusign_preview_not_enabled:
    'DocuSign preview is not enabled for this account',
  err_default_esign_envelope_missing:
    'Set up the default DocuSign signature flow in settings first',
};

/* -------------------------------------------------------------------------- *
 * DocuSign as a stage activity (flag: t_docusign_on_all_stages)
 *
 * Distinct from the offer e-sign flow above: this is an activity you add to any
 * interview-plan stage, carrying its own uploaded .docx documents. It shares the
 * offer flow's email model (per-recipient toggle, CC recipients) but none of its
 * offer tokens — a stage document only carries the six anchors below.
 * -------------------------------------------------------------------------- */

/** `kind` on the stage-action payload. Matches the other activity kinds (email, survey…). */
export const ESIGN_ACTIVITY_KIND = 'esign';

/** Server default for `label`; sent explicitly so the plan row never renders blank. */
export const ESIGN_ACTIVITY_LABEL = 'Docusign e-sign document';

export const ESIGN_DOCUMENT_LIMITS = {
  MAX_FILES: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  // react-dropzone `accept` map. Extension alone is not enough — some browsers
  // report .docx with an empty MIME type, so both are listed.
  ACCEPTED_FILES: {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
  },
} as const;

// The supported-token catalogue used to live here as six entries. It is now
// ~72 and served by GET /esign_documents/supported_tokens — see
// `useEsignSupportedTokens`. The required anchor comes from that response's
// `required` flag rather than a second constant, so the rail and the save gate
// cannot drift apart.

/** DocuSign's own cap on a custom email body. */
export const ESIGN_BODY_MAX_LENGTH = 10000;

/**
 * DOM anchors on the e-sign composer's sections.
 *
 * The stage-activity wizard renders a left rail that scrolls to each section
 * (Figma 64:11459 / 37:7995). The composer owns the sections, so it stamps
 * these ids on them and the rail reads back whichever are currently visible —
 * that way the rail follows the composer's own show/hide rules instead of
 * duplicating them.
 */
export const ESIGN_SECTION_ANCHOR_ATTR = 'data-esign-section';
export const ESIGN_SECTION_TITLE_ATTR = 'data-esign-section-title';

export const ESIGN_ACTIVITY_STEPS = {
  DOCUMENTS: 'documents',
  DETAILS: 'details',
} as const;

export const ESIGN_ACTIVITY_COPY = {
  DRAWER_TITLE: ESIGN_ACTIVITY_LABEL,
  PREVIEW_TITLE: 'Preview',
  PREVIEW_UNAVAILABLE: 'This document is not available to preview yet',
  TOKEN_INFO: 'Info',
  PICKER_DESCRIPTION:
    'Send documents like NDAs to candidates for e-signature, set the company signer and signing order',
  STEP_ONE_LABEL: 'Upload documents',
  STEP_TWO_LABEL: 'Add details',
  // The same banner shape as the wizard's, for the admin settings drawer
  // reached from Organization integrations (Figma 121:14227). It says what
  // this screen covers and, just as importantly, what it does not: NDAs and
  // everything else are configured per stage, not here.
  SETTINGS_BANNER_TITLE: 'Docusign for offers, NDAs and other e-sign documents',
  SETTINGS_BANNER_BODY:
    'Configure how offers go out for e-signature here. For NDAs and other documents, add a DocuSign activity to any stage.',
  SETTINGS_BANNER_LINK: 'See how it works',
  HOW_IT_WORKS_TITLE: 'How it works?',
  HOW_IT_WORKS_BODY:
    'Upload .docx documents with Docusign tokens included, ready for candidates to e-sign.',
  LEARN_MORE: 'Learn more',
  UPLOAD_MAIN: 'Drag and drop files or',
  UPLOAD_LINK: 'click to upload',
  UPLOAD_HELPER: 'Supported files: .docx, up to 10 MB',
  TAB_AVAILABLE: 'Available tokens',
  TAB_INCLUDED: 'Tokens included',
  TOKENS_UNAVAILABLE:
    'Token list unavailable right now. You can still upload documents — Docusign checks their tokens when you continue.',
  LOADING: 'Loading…',
  AVAILABLE_TOKENS_CAPTION: 'Tokens supported in documents',
  INCLUDED_TOKENS_CAPTION: 'Tokens detected in the documents uploaded',
  INCLUDED_TOKENS_EMPTY: 'Upload a document to see the included tokens',
  VALID_TOKENS_HEADING: 'Valid tokens included',
  NO_TOKENS_FOUND: 'No tokens found',
  TOKENS_NOT_RECORDED:
    "This document's tokens weren't recorded. Upload it again to see them.",
  // A document the server rejected without naming a single token. The red
  // badge over an empty list said something was wrong and nothing about what
  // — this is the fallback when the payload carries no `err_*` code either.
  VALIDATION_UNREADABLE:
    'Docusign could not read the tokens in this document. Re-upload it, and contact support if it keeps failing.',
  INVALID_TOKENS_HEADING: 'Invalid tokens included',
  EMPTY_TOKENS_HEADING: 'Empty tokens included',
  EMPTY_TOKENS_SEND_HINT:
    'These tokens have no value for this candidate. Add the missing details or remove this document to continue.',
  STATUS_VERIFIED: 'Verified',
  STATUS_INVALID: 'Invalid tokens',
  STATUS_EMPTY: 'Empty tokens',
  NEXT: 'Next: Add details',
  BACK: 'Back',
  CANCEL: 'Cancel',
  SAVE: 'Save',
  PREVIEW: 'Preview on Docusign',
  CONTINUE_IN_DOCUSIGN: 'Continue in Docusign',
  DOCUMENTS_REQUIRED: 'Upload at least one document to continue',
  INVALID_DOCUMENTS_PRESENT:
    'Remove or replace the documents with invalid tokens to continue',
  EMPTY_TOKENS_PRESENT:
    'Add the missing details or remove the documents with empty tokens to continue',
  SIGNATURE_ANCHOR_MISSING:
    'No signature field found. Add one in Preview on Docusign, or the candidate can add it while signing.',
  COUNT_LIMIT: `You can upload up to ${ESIGN_DOCUMENT_LIMITS.MAX_FILES} documents`,
  SAVE_SUCCESS: 'Docusign activity saved',
  SAVE_ERROR: 'Unable to save the Docusign activity',
  VALIDATE_ERROR: "We couldn't check those documents. Please try again.",
  PREVIEW_ERROR: 'Unable to open the Docusign preview, please try again',
  // The two actions on a live contract. Both void the envelope in Docusign;
  // "Edit" then reopens the wizard so a replacement can go out. There is no
  // un-void, and Docusign emails everyone a cancellation notice — both worth
  // saying before the recruiter commits.
  DELETE_ACTION: 'Delete document',
  DELETE_CONFIRM_TITLE: 'Delete this document?',
  DELETE_CONFIRM_BODY:
    'Docusign will void the document so it can no longer be signed, and will email everyone it was sent to. This cannot be undone.',
  // The modal's own dismiss button says "Cancel" by default, which reads as a
  // second way to do the same thing next to a delete.
  DELETE_CONFIRM_SUBMIT: 'Delete document',
  DELETE_CONFIRM_DISMISS: 'Keep document',

  // Docusign has no reminder API — this re-sends the same envelope, so the
  // signer gets the original signing email again. No count, no history.
  REMINDER_SENT: 'Reminder sent',

  EDIT_ACTION: 'Edit document',
  EDIT_CONFIRM_TITLE: 'Edit this document?',
  // Voiding first is not a choice: Docusign allows one live contract per
  // candidate, so a replacement cannot be sent while this one stands.
  EDIT_CONFIRM_BODY:
    'The document already sent will be voided so a new one can go out, and Docusign will email everyone it was sent to. You will be able to review the new document before it is sent.',
  EDIT_CONFIRM_SUBMIT: 'Void and edit',
  EDIT_CONFIRM_DISMISS: 'Keep document',

  DISCARD_CONFIRM_TITLE: 'Discard this document?',
  DISCARD_CONFIRM_BODY: 'Your changes will be lost.',
  DISCARD_AFTER_VOID_BODY:
    'The previous document was voided, so the candidate has nothing to sign until this one is sent.',
  DISCARD_CONFIRM_SUBMIT: 'Discard',
  DISCARD_CONFIRM_DISMISS: 'Keep editing',

  VOID_SUCCESS: 'Contract cancelled',
  VOID_ERROR: 'Unable to cancel the contract, please try again',
} as const;

/**
 * User-facing copy for the `err_*` codes the esign stage-action endpoints return.
 * Same shape as ADD_DOCUMENT_ERROR_COPY on the offer side: unknown codes fall
 * back to generic copy rather than leaking a server string into a toast.
 */
export const ESIGN_ACTIVITY_ERROR_COPY: Record<string, string> = {
  // Cancelling a contract (POST /esign_envelopes/:id/void).
  err_esign_envelope_not_remindable:
    'This contract can no longer be reminded on — it has already been completed, refused or cancelled.',
  err_esign_envelope_not_voidable:
    'This contract can no longer be cancelled — it has already been completed, refused or cancelled.',
  err_esign_send_already_in_progress:
    'An e-sign request is already in progress for this candidate. Delete or complete it before starting a new one.',
  err_esign_envelope_not_found: 'This contract no longer exists.',
  err_voided_reason_required: 'Add a reason for cancelling, or leave it blank.',
  err_api_failure: 'Docusign could not be reached. Try again in a moment.',
  err_esign_activity_not_enabled:
    "Docusign activities aren't enabled for your account.",
  err_esign_documents_required: 'Upload at least one document.',
  err_esign_document_count_limit_exceeded: `An activity can have at most ${ESIGN_DOCUMENT_LIMITS.MAX_FILES} documents.`,
  err_esign_document_type_invalid:
    'Only .docx files up to 10 MB can be uploaded.',
  err_esign_document_unreadable:
    "We couldn't read that .docx. Re-save it and try again.",
  err_esign_document_invalid_tokens:
    'One or more documents contain tokens that Docusign cannot use.',
  err_esign_document_empty_tokens:
    'Some tokens in the documents have no value for this candidate. Fill them in, then try again.',
  // The server rejects a set with no candidate signature anchor. Named
  // literally here because this maps a server code, where the wizard's own
  // pre-check names whichever anchor the catalogue marks required.
  err_esign_candidate_signature_missing:
    'Add the candidate signature anchor to at least one document — without it the candidate has nowhere to sign.',
  err_esign_invalid_signing_order: 'Choose a valid signing order.',
  err_esign_company_signer_not_found:
    'The chosen company signer is no longer an active user.',
  err_esign_candidate_email_subject_required:
    'Candidate email subject is required.',
  err_esign_company_email_subject_required:
    'Company signer email subject is required.',
  err_esign_cc_email_subject_required:
    'Copy recipient email subject is required.',
  err_esign_email_body_too_long: `Email bodies must be under ${ESIGN_BODY_MAX_LENGTH.toLocaleString()} characters.`,
  err_invalid_cc_user_ids: 'One of the copy recipients is no longer active.',
  err_docusign_not_enabled: 'Connect Docusign before sending documents.',
  err_esign_action_kind_invalid: 'This activity is not a Docusign activity.',
  err_esign_preview_not_ready:
    'The Docusign draft is still being prepared. Try again in a moment.',
};

/**
 * In-app notification card for the six e-sign events (RTC types 126-131).
 *
 * The titles are NOT here: they arrive fully rendered from the server, so the
 * card never interpolates names. Only the card's own chrome lives here.
 */
export const ESIGN_NOTIFICATION_COPY = {
  DETAILS: 'Details',
  CANDIDATE: 'Candidate',
  DOCUMENT: 'Document',
  JOB_NAME: 'Job name',
  STAGE_NAME: 'Stage name',
  GO_TO_CANDIDATE: 'Go to candidate',
} as const;

/**
 * RTC's integer ids for the e-sign events. The notification list dispatches on
 * the integer, not the Core-side string, and an unmapped type renders NOTHING
 * at all — so every one of these has to be registered or the card silently
 * never appears.
 */
export const ESIGN_NOTIFICATION_TYPES = {
  SIGNED_BY_CANDIDATE: 126,
  SIGNED_BY_EMPLOYER: 127,
  SIGNED_BY_BOTH: 128,
  SIGNED_CANDIDATE_ONLY: 129,
  REFUSED_BY_CANDIDATE: 130,
  REFUSED_BY_EMPLOYER: 131,
} as const;

/**
 * The e-sign drawers all sit at the interview plan's 1067px (Figma 2:3385).
 * A literal so Tailwind's scanner still sees the arbitrary value; shared so the
 * preview cannot drift off the wizard again (CORE-5408 item 3 — it was 1034).
 */
export const ESIGN_DRAWER_WIDTH_CLASS = '!max-w-[1067px]';

/**
 * Figma's "Generic Slider" chrome (2:17749): a 16px top-left corner and a 48px
 * hairline-bordered close button, 24px down and 24px off the panel. kula's
 * Drawer draws 12px and a borderless 40px button at 16px/12px, with no props
 * for either — worth raising upstream.
 */
export const ESIGN_DRAWER_CHROME_CLASS =
  "!rounded-tl-2xl [&>button[aria-label='Close']]:!size-12 [&>button[aria-label='Close']]:!top-6 [&>button[aria-label='Close']]:!mr-6 [&>button[aria-label='Close']]:!border-[0.75px] [&>button[aria-label='Close']]:!border-solid [&>button[aria-label='Close']]:!border-secondary-100";

/** The same component's scrim is 40% black; kula's is 25%. */
export const ESIGN_DRAWER_OVERLAY_CLASS = '!bg-black/40';
